import { request } from '../../../api';
import type { ConsentsPayload } from '../api';

/**
 * Источники карточки клиента.
 *
 * Карточка собирается из восьми запросов, а не из одного агрегата: каждый модуль
 * сам знает, что именно он умеет показывать этой роли, и сам отвечает 403, если
 * не умеет. Один сводный маршрут потребовал бы кросс-модульного SQL из
 * идентификации по чужим таблицам — так уже сделано ровно в одном месте
 * (сравнение при склейке), и расширять этот приём не стоит.
 *
 * Секции грузятся независимо: медленная или недоступная не задерживает и не
 * роняет остальные.
 */

export interface CardCustomer {
  readonly id: string;
  readonly displayName: string | null;
  readonly phone: string | null;
  readonly status: string;
}

/** Строки визитов и автомобилей приходят из ядра сырыми, в змеином регистре. */
export interface CardVehicleRow {
  readonly id: string;
  readonly plate: string;
  readonly make: string | null;
  readonly model: string | null;
  readonly body_type: string | null;
  readonly size_class: string | null;
  readonly coating_notes: string | null;
  readonly role: string;
  readonly valid_from: string;
  readonly valid_to: string | null;
}

export interface CardVisitRow {
  readonly id: string;
  readonly site_id: string;
  readonly entered_at: string;
  readonly identification_method: string;
}

/** Атрибуты профиля: тип, юрлицо, подтверждение телефона, даты, риск оттока. */
export interface CardProfileFacts {
  readonly type: 'individual' | 'company';
  readonly bin: string | null;
  readonly email: string | null;
  readonly companyId: string | null;
  readonly companyName: string | null;
  readonly phoneVerifiedAt: string | null;
  readonly firstVisitAt: string | null;
  readonly lastVisitAt: string | null;
  readonly churnScore: number | null;
  readonly churnScoredAt: string | null;
  readonly source: string;
  readonly createdAt: string;
}

export interface CardProfile {
  readonly customer: CardCustomer;
  readonly profile: CardProfileFacts | null;
  readonly vehicles: readonly CardVehicleRow[];
  readonly visits: readonly CardVisitRow[];
  /** Курсор следующей страницы визитов; `null` — показаны все. */
  readonly visitsNextCursor: string | null;
}

export interface VisitsPage {
  readonly items: readonly CardVisitRow[];
  readonly nextCursor: string | null;
}

export const VEHICLE_BODY_TYPES = [
  'sedan',
  'hatchback',
  'wagon',
  'coupe',
  'suv',
  'pickup',
  'van',
  'minibus',
  'bus',
  'truck',
  'motorcycle',
] as const;

export interface VehiclePatch {
  readonly make?: string | null;
  readonly model?: string | null;
  readonly bodyType?: string | null;
  readonly sizeClass?: string | null;
  readonly coatingNotes?: string | null;
}

export interface TierProgress {
  readonly code: string;
  readonly name: string;
  readonly missingVisits?: number;
  readonly missingSpendMinor?: number;
  readonly missingAccruedMinor?: number;
}

export interface OfferRule {
  readonly ruleId: string;
  readonly code: string;
  readonly version: number;
  readonly name: string;
  readonly serviceCodes: readonly string[];
  readonly minAmountMinor: number | undefined;
}

export interface OfferBundle {
  readonly customerId: string;
  readonly balanceMinor: number;
  readonly currentTier: {
    readonly id: string;
    readonly code: string;
    readonly name: string;
  } | null;
  readonly nextTier: TierProgress | null;
  readonly offers: readonly OfferRule[];
}

export interface CardAccount {
  readonly accountId: string;
  readonly balanceMinor: number;
  readonly materializedMinor: number;
  readonly driftMinor: number;
  readonly currency: string;
}

export interface CardSubscription {
  readonly id: string;
  readonly planCode: string;
  readonly planPeriod: string;
  readonly priceMinor: number;
  readonly status: string;
  readonly startedAt: string | null;
  readonly currentPeriodEnd: string | null;
  readonly autorenew: boolean;
  readonly cancelReason: string | null;
}

export interface CardOrder {
  readonly id: string;
  readonly siteId: string;
  readonly clientOrderNo: number;
  readonly totalMinor: number;
  readonly discountMinor: number;
  readonly status: string;
  readonly createdAt: string;
}

export interface CardTransaction {
  readonly id: string;
  readonly provider: string;
  readonly kind: string;
  readonly amountMinor: number;
  readonly status: string;
  readonly failureReason: string | null;
  readonly createdAt: string;
}

export interface CardFeedback {
  readonly id: string;
  readonly channel: string;
  readonly score: number | null;
  readonly body: string | null;
  readonly category: string | null;
  readonly status: string;
  readonly dueAt: string | null;
  readonly overdue: boolean;
  readonly createdAt: string;
}

export function profile(customerId: string): Promise<CardProfile> {
  return request<CardProfile>('GET', `/api/v1/customers/${encodeURIComponent(customerId)}`);
}

export function offers(customerId: string): Promise<OfferBundle> {
  return request<OfferBundle>('GET', `/api/v1/loyalty/offers/${encodeURIComponent(customerId)}`);
}

/**
 * Счёта может не быть вовсе — тогда сервер честно отвечает «не найден».
 * Для карточки это не ошибка, а нулевой баланс: клиент просто ещё ничего не
 * накопил, и красная плашка на этом месте только пугала бы.
 */
export async function account(customerId: string): Promise<CardAccount | null> {
  try {
    return await request<CardAccount>(
      'GET',
      `/api/v1/loyalty/accounts/${encodeURIComponent(customerId)}`,
    );
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

export async function subscriptions(customerId: string): Promise<readonly CardSubscription[]> {
  const data = await request<{ subscriptions: readonly CardSubscription[] }>(
    'GET',
    `/api/v1/subscriptions?customer_id=${encodeURIComponent(customerId)}`,
  );
  return data.subscriptions;
}

export function orders(customerId: string): Promise<readonly CardOrder[]> {
  return request<readonly CardOrder[]>(
    'GET',
    `/api/v1/pos/orders?customer_id=${encodeURIComponent(customerId)}`,
  );
}

export async function transactions(customerId: string): Promise<readonly CardTransaction[]> {
  const page = await request<{ items: readonly CardTransaction[] }>(
    'GET',
    `/api/v1/payments/transactions?customer_id=${encodeURIComponent(customerId)}`,
  );
  return page.items;
}

export async function feedback(customerId: string): Promise<readonly CardFeedback[]> {
  const page = await request<{ items: readonly CardFeedback[] }>(
    'GET',
    `/api/v1/feedback?customer_id=${encodeURIComponent(customerId)}`,
  );
  return page.items;
}

export function consents(customerId: string): Promise<ConsentsPayload> {
  return request<ConsentsPayload>(
    'GET',
    `/api/v1/identity/customers/${encodeURIComponent(customerId)}/consents`,
  );
}

export function visitsPage(customerId: string, cursor: string): Promise<VisitsPage> {
  return request<VisitsPage>(
    'GET',
    `/api/v1/customers/${encodeURIComponent(customerId)}/visits?limit=50&cursor=${encodeURIComponent(cursor)}`,
  );
}

export function updateProfile(
  customerId: string,
  patch: { readonly displayName?: string | null; readonly email?: string | null },
): Promise<unknown> {
  return request('PATCH', `/api/v1/customers/${encodeURIComponent(customerId)}`, patch);
}

export function updateVehicle(vehicleId: string, patch: VehiclePatch): Promise<unknown> {
  return request('PATCH', `/api/v1/vehicles/${encodeURIComponent(vehicleId)}`, patch);
}

/** Код подтверждения на телефон клиента (ТЗ §5.3.2). */
export function requestPhoneCode(phone: string): Promise<{ expiresAt: string; reused: boolean }> {
  return request('POST', '/api/v1/comms/otp/request', { phone, purpose: 'confirm' });
}

export function verifyPhoneCode(phone: string, code: string): Promise<{ verified: true }> {
  return request('POST', '/api/v1/comms/otp/verify', { phone, code, purpose: 'confirm' });
}
